import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { useCatalogSelectionInfo } from '~/features/catalog/hooks/useCatalogSelectionInfo.ts';
import {
  QUERY_SEARCH_PARAM_CATALOG_AGRID,
  QUERY_SEARCH_PARAM_CATALOG_HAGRID,
  QUERY_SEARCH_PARAM_CATALOG_HWGID
} from '~/features/catalog/mappers/catalogSearchQueryMapper.ts';
import { useEffect, useMemo, useRef } from 'react';
import { HouseIcon } from 'lucide-react';
import { useBasePath } from '~/common/hooks/useBasePath.ts';
import { useScreenSize } from '~/common/hooks/useScreenSize.ts';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator
} from '~/common/components/ui/breadcrumb.tsx';
import { cn } from '~/common/utils/styleUtil.ts';
import type { BreadcrumbItemInfo } from '~/common/components/BreadcrumbItemInfo.ts';
import { useHydrated } from 'remix-utils/use-hydrated';
import { useRootLoaderData } from '../utils/useRootLoaderData';
import { BreadcrumbBackButton } from '~/common/components/BreadcrumbBackButton';

export function CatalogIndexBreadcrumbs() {
  const rootLoaderData = useRootLoaderData();
  const { t } = useTranslation();
  const { path } = useBasePath();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { hagrId, agr, agrId, hagr, hwgId, hwg } = useCatalogSelectionInfo(rootLoaderData.catalogItems.items);
  const location = useLocation();
  const { screenSize, isMobile } = useScreenSize();
  const isHydrated = useHydrated();
  const breadcrumbs = useMemo(() => {
    const linkSearchParams = new URLSearchParams();
    const items: BreadcrumbItemInfo[] = [
      {
        key: 'startseite',
        icon: <HouseIcon size={14} />,
        name: t('Startseite'),
        link: `${path}`
      }
    ];

    items.push({ key: 'gesamtsortiment', name: t('Gesamtsortiment'), link: `${path}/catalog` });

    if (hwgId && hwg) {
      linkSearchParams.set(QUERY_SEARCH_PARAM_CATALOG_HWGID, hwgId.toString());
      items.push({ key: hwgId, name: hwg?.name, link: `${location.pathname}?${linkSearchParams}` });
    }

    if (hagrId && hagr) {
      linkSearchParams.set(QUERY_SEARCH_PARAM_CATALOG_HAGRID, hagrId.toString());
      items.push({ key: hagrId, name: hagr.name, link: `${path}/catalog?${linkSearchParams}` });
    }

    if (agrId && agr) {
      linkSearchParams.set(QUERY_SEARCH_PARAM_CATALOG_AGRID, agrId.toString());
      items.push({ key: agrId, name: agr.name, link: `${path}/catalog?${linkSearchParams}` });
    }

    return items;
  }, [t, path, hwgId, hwg, hagrId, hagr, location.pathname, agrId, agr]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      container.scrollLeft = container.scrollWidth;
    }
  }, [breadcrumbs, screenSize.width]);

  return (
    <div className="flex items-center gap-2 pt-4 desktop:pt-0">
      <BreadcrumbBackButton />
      <div
        className={cn('overflow-x-auto no-scrollbar')}
        style={{ width: isHydrated && isMobile ? `${screenSize.width - 32}px` : 'auto' }}
        ref={scrollContainerRef}
      >
        <Breadcrumb>
          <BreadcrumbList className="flex-nowrap">
            {breadcrumbs.map((item, index) => (
              <div key={item.key} className="flex items-center">
                {index > 0 && <BreadcrumbSeparator className={'pr-1'} />}
                <BreadcrumbItem className="whitespace-nowrap">
                  <BreadcrumbLink asChild>
                    <Link
                      to={item.link}
                      className="flex items-center gap-1 hover:text-primary text-base"
                      data-testid={`breadcrumb-${item.link}`}
                    >
                      {item.icon}
                      {item.name}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </div>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
    </div>
  );
}
